/**
 * Pure projector that turns a room's ``lifecycleStatus`` (+ optional
 * ``inactivityWarning``) into the UI flags the chat surface and the
 * sidebar need to render correctly.
 *
 * The chat experience is split across three components — sidebar row,
 * chat banner, composer / approval bar. They all need the same
 * decisions:
 *
 *   * Should the room appear in the standard user's list?
 *   * Should the room name render in red (an inactive-warning state)?
 *   * Should the prompt-box / approval bar be frozen?
 *   * What banner copy goes above the message stream?
 *   * What badge tone + label goes on the sidebar row (admins only)?
 *
 * Centralising the rules here keeps the components declarative ("render
 * given a state object") and makes every variant unit-testable without
 * mounting the DOM. The matching backend rules live in
 * ``backend/models/pool_lifecycle.py`` (``is_frozen``,
 * ``is_hidden_from_users``, ``inactivity_warning_payload``); changes to
 * either side should land together.
 */
import { inactivityBannerText } from './poolLifecycleBuckets';

// ---------------------------------------------------------------------------
// Status constants — kept verbatim so a grep for either name lands here
// alongside the bucket helpers.
// ---------------------------------------------------------------------------

export const LIFECYCLE_STATUS_ACTIVE = 'ACTIVE';
export const LIFECYCLE_STATUS_NOTIFIED = 'NOTIFIED';
export const LIFECYCLE_STATUS_PAUSED = 'PAUSED';
export const LIFECYCLE_STATUS_RETIRED = 'RETIRED';
export const LIFECYCLE_STATUS_ARCHIVED = 'ARCHIVED';

const _FROZEN = new Set([
  LIFECYCLE_STATUS_PAUSED,
  LIFECYCLE_STATUS_RETIRED,
  LIFECYCLE_STATUS_ARCHIVED,
]);

const _HIDDEN_FROM_USERS = new Set([
  LIFECYCLE_STATUS_RETIRED,
  LIFECYCLE_STATUS_ARCHIVED,
]);

/** UI-state token mirrored into the chat reducer alongside RUNNING /
 *  AWAITING_APPROVAL. The composer + approval bar treat it as terminal:
 *  no edits, no sends, no claims. */
export const UI_STATE_FROZEN = 'FROZEN';

// ---------------------------------------------------------------------------
// Status normalisation
// ---------------------------------------------------------------------------

/**
 * Coerce a raw status (from REST or AppSync) to one of the five canonical
 * statuses. Returns ``ACTIVE`` for anything we don't recognise so a stale
 * client doesn't accidentally freeze rooms it can't reason about.
 *
 * @param {string | null | undefined} raw
 * @returns {'ACTIVE'|'NOTIFIED'|'PAUSED'|'RETIRED'|'ARCHIVED'}
 */
export function normalizeLifecycleStatus(raw) {
  const s = (raw || '').toString().trim().toUpperCase();
  if (
    s === LIFECYCLE_STATUS_ACTIVE
    || s === LIFECYCLE_STATUS_NOTIFIED
    || s === LIFECYCLE_STATUS_PAUSED
    || s === LIFECYCLE_STATUS_RETIRED
    || s === LIFECYCLE_STATUS_ARCHIVED
  ) {
    return s;
  }
  return LIFECYCLE_STATUS_ACTIVE;
}

// ---------------------------------------------------------------------------
// Projector
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} RoomLifecycleInput
 * @property {string=} lifecycleStatus    Server-attached canonical status.
 * @property {string=} status             Legacy fallback (pre-M2 servers).
 * @property {{ daysUntilRetire?: number|string|null }=} inactivityWarning
 *   Server-attached warning payload — only present for ``NOTIFIED`` rows.
 */

/**
 * @typedef {Object} ProjectorOptions
 * @property {boolean=} isAdmin    Caller is a workspace OWNER / ADMIN.
 *   Admins see ``RETIRED`` + ``ARCHIVED`` rooms in the sidebar (with a
 *   muted badge + Restore action) where regular members don't.
 */

/**
 * @typedef {Object} LifecycleUiState
 * @property {'ACTIVE'|'NOTIFIED'|'PAUSED'|'RETIRED'|'ARCHIVED'} status
 * @property {boolean} frozen           Composer + approval bar locked.
 * @property {boolean} hidden           Drop from non-admin sidebar lists.
 * @property {boolean} redName          Render the sidebar row name in red.
 * @property {string|null} bannerText   Persistent banner above the chat.
 *                                       ``null`` means render no banner.
 * @property {'info'|'warning'|'danger'|null} bannerTone
 * @property {string} disabledReason    Tooltip / placeholder copy for
 *                                       frozen composer rows. Empty string
 *                                       when the composer is editable.
 * @property {{ label: string, tone: string }|null} adminBadge
 *   Pill rendered next to the room name in the admin sidebar overlay.
 *   ``null`` for ACTIVE rows so the regular green dot wins.
 * @property {string} uiState           One of ``''`` or ``UI_STATE_FROZEN``;
 *                                       feeds the existing UI_STATE machine.
 */

const _BANNER_TONES = Object.freeze({
  [LIFECYCLE_STATUS_NOTIFIED]: 'danger',
  [LIFECYCLE_STATUS_PAUSED]: 'warning',
  [LIFECYCLE_STATUS_RETIRED]: 'muted',
  [LIFECYCLE_STATUS_ARCHIVED]: 'muted',
});

const _ADMIN_BADGE = Object.freeze({
  [LIFECYCLE_STATUS_NOTIFIED]: { label: 'Notified', tone: 'danger' },
  [LIFECYCLE_STATUS_PAUSED]: { label: 'Paused', tone: 'warning' },
  [LIFECYCLE_STATUS_RETIRED]: { label: 'Retired', tone: 'muted' },
  [LIFECYCLE_STATUS_ARCHIVED]: { label: 'Archived', tone: 'muted' },
});

/**
 * Project a room into the flags the chat / sidebar surfaces consume.
 *
 * The projector is fully pure:
 *   * No DOM access.
 *   * No service calls.
 *   * No reliance on global feature flags (admins are passed in).
 *
 * Order of precedence for the banner / disabled reason matches the
 * backend's ``is_frozen`` rule: PAUSED > RETIRED > ARCHIVED > NOTIFIED.
 *
 * @param {RoomLifecycleInput | null | undefined} room
 * @param {ProjectorOptions} [opts]
 * @returns {LifecycleUiState}
 */
export function projectRoomLifecycleUiState(room, opts = {}) {
  const isAdmin = Boolean(opts && opts.isAdmin);
  const status = normalizeLifecycleStatus(
    (room && (room.lifecycleStatus || room.status)) || '',
  );

  const frozen = _FROZEN.has(status);
  const hiddenForUsers = _HIDDEN_FROM_USERS.has(status);
  const hidden = hiddenForUsers && !isAdmin;
  const redName = status === LIFECYCLE_STATUS_NOTIFIED;

  let bannerText = null;
  let bannerTone = null;
  let disabledReason = '';

  if (status === LIFECYCLE_STATUS_NOTIFIED) {
    // Always pass a non-null payload so the helper produces fallback
    // copy when the server didn't attach ``inactivityWarning`` (e.g.
    // a stale older deploy) — NOTIFIED rooms always deserve a banner.
    bannerText = inactivityBannerText((room && room.inactivityWarning) || {});
    bannerTone = _BANNER_TONES[LIFECYCLE_STATUS_NOTIFIED];
  } else if (status === LIFECYCLE_STATUS_PAUSED) {
    bannerText = 'This network is paused by a workspace admin. Activity is frozen until it is resumed.';
    bannerTone = _BANNER_TONES[LIFECYCLE_STATUS_PAUSED];
    disabledReason = 'Paused by an admin — sending is disabled.';
  } else if (status === LIFECYCLE_STATUS_RETIRED) {
    bannerText = 'This network has been retired. It is read-only — ask an admin to restore it to send new prompts.';
    bannerTone = _BANNER_TONES[LIFECYCLE_STATUS_RETIRED];
    disabledReason = 'Retired — sending is disabled.';
  } else if (status === LIFECYCLE_STATUS_ARCHIVED) {
    bannerText = 'This network is archived (long-term cold storage). It is read-only — ask an admin to restore it.';
    bannerTone = _BANNER_TONES[LIFECYCLE_STATUS_ARCHIVED];
    disabledReason = 'Archived — sending is disabled.';
  }

  const adminBadge = _ADMIN_BADGE[status] || null;

  return {
    status,
    frozen,
    hidden,
    redName,
    bannerText: bannerText || null,
    bannerTone: bannerTone || null,
    disabledReason,
    adminBadge,
    uiState: frozen ? UI_STATE_FROZEN : '',
  };
}

/**
 * Convenience predicate — used by the chat composer to short-circuit
 * its existing IDLE / RUNNING / AWAITING_APPROVAL gates.
 *
 * @param {RoomLifecycleInput | null | undefined} room
 */
export function isRoomFrozen(room) {
  return projectRoomLifecycleUiState(room).frozen;
}

/**
 * Per-row admin actions for the agentic sidebar.
 *
 * The Lifecycle tab in the workbench is the canonical place to drive
 * pool transitions, but it's two clicks away — open Workbench → open
 * the right network → switch tabs. Admins frequently want to *just*
 * resume a paused room or restore a retired one from where they
 * already are. We surface a tiny per-row menu in the sidebar with the
 * single relevant action for the row's current state.
 *
 * Decisions baked in:
 *
 *   * The list is always empty for non-admins — regular members never
 *     drive lifecycle from the sidebar; the rendered hover button is
 *     gated on ``actions.length > 0``.
 *   * ``ACTIVE`` and ``NOTIFIED`` rooms have no menu — sending a
 *     prompt is the resume signal, not a button press.
 *   * ``PAUSED`` admins resume (back to ACTIVE).
 *   * ``RETIRED`` / ``ARCHIVED`` admins restore (back to ACTIVE).
 *
 * The action ids match the verbs used by ``rest_handlers`` and the
 * Lifecycle tab so a single ``onRoomLifecycleAction`` dispatcher can
 * service both surfaces.
 *
 * Pure: no DOM, no service calls.
 *
 * @param {RoomLifecycleInput | null | undefined} room
 * @param {ProjectorOptions} [opts]
 * @returns {Array<{
 *   id: 'unpause' | 'restore',
 *   label: string,
 *   tone: 'default' | 'primary',
 *   confirmText: string,
 *   successText: string,
 * }>}
 */
export function getRoomLifecycleActions(room, opts = {}) {
  const isAdmin = Boolean(opts && opts.isAdmin);
  if (!isAdmin) return [];
  const status = normalizeLifecycleStatus(
    (room && (room.lifecycleStatus || room.status)) || '',
  );
  const label = (room && room.displayName)
    || (room && `${room.networkId} · ${room.poolId}`)
    || 'this room';
  if (status === LIFECYCLE_STATUS_PAUSED) {
    return [{
      id: 'unpause',
      label: 'Resume room',
      tone: 'primary',
      confirmText: `Resume "${label}"? It will go back to Active and the prompt box will unlock.`,
      successText: `Resumed ${label}.`,
    }];
  }
  if (
    status === LIFECYCLE_STATUS_RETIRED
    || status === LIFECYCLE_STATUS_ARCHIVED
  ) {
    return [{
      id: 'restore',
      label: 'Restore room',
      tone: 'primary',
      confirmText: `Restore "${label}"? It will become Active again and reappear for users.`,
      successText: `Restored ${label}.`,
    }];
  }
  return [];
}

/**
 * Filter a room list down to the entries the caller should see in the
 * sidebar. Non-admins drop ``RETIRED`` / ``ARCHIVED`` rows entirely;
 * admins see every row so they can drive Restore from the sidebar.
 *
 * Stable: preserves input order. Returns ``[]`` for non-array input so
 * the consumer can call ``.map`` without defensive checks.
 *
 * @template T
 * @param {T[]} rooms
 * @param {ProjectorOptions} [opts]
 * @returns {T[]}
 */
export function filterRoomsForSidebar(rooms, opts = {}) {
  if (!Array.isArray(rooms) || rooms.length === 0) return [];
  const isAdmin = Boolean(opts && opts.isAdmin);
  if (isAdmin) return rooms.slice();
  const out = [];
  for (const r of rooms) {
    const status = normalizeLifecycleStatus(
      (r && (r.lifecycleStatus || r.status)) || '',
    );
    if (_HIDDEN_FROM_USERS.has(status)) continue;
    out.push(r);
  }
  return out;
}
